import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Safeguard } from "@scenes/safeguard";
import { ResourceAvatar } from "@common/resourceAvatar";
import { ContextualMenu } from "@common/contextualMenu";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { Props } from "./types";
import { AssignCostProfileToResource } from "./assignCostProfileToResource";
import styles from "./styles.scss";

function Resource({ resource }: Props) {
  const { pushView } = useModal();
  const { t } = useTranslation();
  const { name, profession } = resource;

  return (
    <Safeguard
      useCases={[
        UseCases.retrieveResource,
        UseCases.removeResourceFromPlanzone,
        UseCases.assignCostProfileToResource,
      ]}
    >
      {({
        canRetrieveResource,
        canRemoveResourceFromPlanzone,
        canAssignCostProfileToResource,
      }) => (
        <div
          className={classNames(styles.resource, {
            [styles.readOnly]: !canRetrieveResource,
          })}
        >
          <div
            className={styles.user}
            onClick={() =>
              canRetrieveResource &&
              pushView({
                data: resource.uuid,
                key: UseCases.retrieveResource,
              })
            }
          >
            <div className={styles.avatar}>
              <ResourceAvatar resource={resource} />
            </div>
            <div>
              <p className={styles.name}>{name}</p>
              {profession && <p className={styles.title}>{profession}</p>}
            </div>
          </div>
          <div className={styles.affiliation}>
            <p>
              {t(
                resource.affiliation === "EXTERNAL"
                  ? "costProfilesPage.sidebar.external"
                  : "costProfilesPage.sidebar.internal",
              )}
            </p>
          </div>
          {canAssignCostProfileToResource ? (
            <div className={styles.costProfile}>
              <AssignCostProfileToResource resource={resource} />
            </div>
          ) : (
            <div className={styles.costProfile}>
              <p>{resource.costProfile && resource.costProfile.name}</p>
            </div>
          )}
          <div>
            {(canRetrieveResource || canRemoveResourceFromPlanzone) && (
              <div className={styles.contextualMenu}>
                <ContextualMenu
                  items={[
                    ...(canRetrieveResource
                      ? [
                          {
                            name: t("costProfilesPage.sidebar.edit"),
                            renderIcon: (className: string) => (
                              <EditIcon className={className} />
                            ),
                            act: () =>
                              pushView({
                                data: resource.uuid,
                                key: UseCases.retrieveResource,
                              }),
                          },
                        ]
                      : []),
                    ...(canRemoveResourceFromPlanzone
                      ? [
                          {
                            name: t("costProfilesPage.sidebar.remove"),
                            renderIcon: (className: string) => (
                              <TrashIcon
                                className={classNames(className, styles.icon)}
                              />
                            ),
                            color: "#db5f63",
                            act: () =>
                              pushView({
                                data: resource,
                                key: UseCases.removeResourceFromPlanzone,
                              }),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Safeguard>
  );
}

export { Resource };
